<div class="container">
  <header class="header">
    <button class="back-button" (click)="goBack()">
      <span class="back-arrow">←</span>
    </button>
    <h1 class="header-text">Privacy Policy</h1>
  </header>
  
  <div class="content">
    <p class="last-updated">Last Updated: {{ lastUpdated }}</p>
    
    <section class="section">
      <h2 class="heading">1. Introduction</h2>
      <p class="paragraph">
        Welcome to Insizon ("we," "our," or "us"). We are committed to protecting your privacy and ensuring you have a positive experience on our dating platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">2. Information We Collect</h2>
      
      <h3 class="sub-heading">2.1 Personal Information</h3>
      <p class="paragraph">
        We collect information that you provide directly to us, including:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Email address and phone number</li>
        <li class="bullet-point">First name and last name</li>
        <li class="bullet-point">Age and gender</li>
        <li class="bullet-point">Location data (address, city, state, country, coordinates)</li>
        <li class="bullet-point">Profile photos (up to 3 images stored securely)</li>
        <li class="bullet-point">Bio text and voice recordings</li>
        <li class="bullet-point">Subscription and payment information</li>
      </ul>

      <h3 class="sub-heading">2.2 Authentication Information</h3>
      <p class="paragraph">
        We support multiple authentication methods:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Email and password authentication</li>
        <li class="bullet-point">Google OAuth (Google ID)</li>
        <li class="bullet-point">Apple Sign In (Apple ID)</li>
      </ul>

      <h3 class="sub-heading">2.3 Usage Data</h3>
      <p class="paragraph">
        We track your daily usage including:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Number of applications</li>
        <li class="bullet-point">Contact form</li>
        <li class="bullet-point">Bug reports</li>
        <li class="bullet-point">Feature request</li>
      </ul>

      <h3 class="sub-heading">2.4 Device Information</h3>
      <p class="paragraph">
        We collect device identifiers and push notification tokens (FCM tokens) to send you notifications about matches, messages, and other app activities.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">3. How We Use Your Information</h2>
      <p class="paragraph">
        We use the information we collect to:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Create and manage your account</li>
        <li class="bullet-point">Facilitate communication between users</li>
        <li class="bullet-point">Send push notifications</li>
        <li class="bullet-point">Process subscription payments and manage premium features</li>
        <li class="bullet-point">Verify your identity through phone and email verification</li>
        <li class="bullet-point">Provide customer support and respond to inquiries</li>
        <li class="bullet-point">Improve our services and develop new features</li>
        <li class="bullet-point">Ensure platform safety and prevent fraud</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">4. Data Storage and Security</h2>
      
      <h3 class="sub-heading">4.1 Storage</h3>
      <p class="paragraph">
        Your data is stored securely:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Profile information and messages are stored in MongoDB databases</li>
        <li class="bullet-point">Photos and voice recordings are stored in Amazon S3 with secure signed URLs</li>
        <li class="bullet-point">All data is encrypted in transit and at rest</li>
      </ul>

      <h3 class="sub-heading">4.2 Security Measures</h3>
      <p class="paragraph">
        We implement industry-standard security measures including:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Secure authentication and authorization</li>
        <li class="bullet-point">Encrypted data transmission (HTTPS/TLS)</li>
        <li class="bullet-point">Regular security audits and updates</li>
        <li class="bullet-point">Access controls and monitoring</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">5. Third-Party Services</h2>
      <p class="paragraph">
        We use the following third-party services that may collect information:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Google OAuth: For Google Sign-In authentication</li>
        <li class="bullet-point">Apple Sign In: For Apple authentication</li>
        <li class="bullet-point">Firebase Cloud Messaging (FCM): For push notifications</li>
        <li class="bullet-point">Stripe: For payment processing and subscription management</li>
        <li class="bullet-point">Amazon S3: For secure file storage</li>
      </ul>
      <p class="paragraph">
        These services have their own privacy policies governing the use of your information.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">6. Your Rights and Choices</h2>
      <p class="paragraph">
        You have the right to:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Access your personal information</li>
        <li class="bullet-point">Update or correct your profile information</li>
        <li class="bullet-point">Delete your account (soft delete - account deactivation)</li>
        <li class="bullet-point">Control notification preferences</li>
        <li class="bullet-point">Opt out of certain data collection where applicable</li>
      </ul>
      <p class="paragraph">
        You can manage these settings in the app or contact us for assistance.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">7. Data Retention</h2>
      <p class="paragraph">
        We retain your information for as long as your account is active or as needed to provide services. When you delete your account, we will deactivate it and mark it with a deletion timestamp. Some information may be retained for legal or legitimate business purposes.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">8. Children's Privacy</h2>
      <p class="paragraph">
        Insizon is intended for users who are 18 years of age or older. We do not knowingly collect personal information from children under 18. If we become aware that we have collected information from a child under 18, we will take steps to delete such information.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">9. Changes to This Privacy Policy</h2>
      <p class="paragraph">
        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
      </p>
    </section>

    <!-- Start of Twilio Compliant SMS Section -->
    <section id="sms-messaging-terms" style="margin-top: 20px; margin-bottom: 20px;">
        <h2>10. SMS Messaging and Mobile Terms</h2>
        <p>By opting in to receive text messages from Insizon, LLC, you agree to the following terms and disclosures:</p>
        <ul>
            <li><strong>Information Sharing:</strong> Mobile numbers and consent information collected for SMS notifications or marketing will not be shared, sold, or rented to third parties or affiliates for marketing or promotional purposes.</li>
            <li><strong>Message Frequency:</strong> Message frequency varies based on your user interaction, account activity, and the number of notifications triggered by your account settings.</li>
            <li><strong>Cost:</strong> Message and data rates may apply for any messages sent to you from us and to us from you.</li>
            <li><strong>Opt-Out:</strong> You can cancel the SMS service at any time by texting "<strong>STOP</strong>" to our number. After you send the SMS message "STOP" to us, we will send you an SMS message to confirm that you have been unsubscribed.</li>
            <li><strong>Support:</strong> If you are experiencing issues with the messaging program, you can reply with the keyword "<strong>HELP</strong>" for more assistance, or you can get help directly at <a href="mailto:support@insizon.com">support@insizon.com</a>.</li>
        </ul>
    </section>
  <!-- End of Twilio Compliant SMS Section -->

    <section class="section">
      <h2 class="heading">11. Contact Us</h2>
      <p class="paragraph">
        If you have any questions about this Privacy Policy or our data practices, please contact us through the Help & Account Settings section in the app or via email at {{environment.SupportEmail}}.
      </p>
    </section>
    
  </div>
</div>